/**
 * Per-user activity timeline drawer.
 *
 * Right-side drawer (shared AppDrawer shell, pattern: AuditLogDrawer) opened
 * from a row click on the User Activity table. Shows bucketed page views
 * interleaved with audit action events, newest first - actions are visually
 * highlighted as the stronger signal.
 */

import React from 'react'
import {
  Box,
  Chip,
  CircularProgress,
  Drawer,
  FormControl,
  MenuItem,
  Select,
  Stack,
  Typography,
} from '@mui/material'
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined'
import BoltIcon from '@mui/icons-material/Bolt'
import {
  AppDrawerShell,
  AppDrawerHeader,
  AppDrawerBody,
  APP_DRAWER_WIDTH,
  APP_DRAWER_BG,
  APP_DRAWER_SHADOW,
} from '../../../../components/shared/AppDrawer'
import { useDataFetching } from '../../../../hooks/useDataFetching'
import {
  activityApi,
  ActivityTimelineResponse,
  ActivityWindow,
  TimelineItem,
  WINDOW_LABELS,
} from '../../../../services/activityApi'
import { formatRelativeTimestamp } from '../../../../utils/dateFormatters'
import { formatDateTime } from '../../../../utils/inspectorFormatters'

interface ActivityTimelineDrawerProps {
  open: boolean
  onClose: () => void
  userId: number | null
  userName?: string
}

const TimelineRow: React.FC<{ item: TimelineItem }> = ({ item }) => {
  const isAction = item.kind === 'action'
  const { label: relative, title } = formatRelativeTimestamp(item.at)
  return (
    <Stack
      direction="row"
      spacing={1.5}
      alignItems="flex-start"
      sx={{
        py: 0.75,
        px: 1,
        borderRadius: 1,
        bgcolor: isAction ? 'success.50' : 'transparent',
        borderLeft: 3,
        borderColor: isAction ? 'success.main' : 'divider',
      }}
    >
      {isAction ? (
        <BoltIcon fontSize="small" color="success" sx={{ mt: 0.25 }} />
      ) : (
        <VisibilityOutlinedIcon fontSize="small" color="disabled" sx={{ mt: 0.25 }} />
      )}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="body2">
          {item.label}
          {!isAction && item.event_count && item.event_count > 1 ? (
            <Typography component="span" variant="caption" color="text.secondary">
              {' '}
              · {item.event_count} requests
            </Typography>
          ) : null}
        </Typography>
        <Typography variant="caption" color="text.secondary" title={title}>
          {relative} - {formatDateTime(item.at)}
        </Typography>
      </Box>
      {isAction && <Chip label="Action" size="small" color="success" variant="outlined" />}
    </Stack>
  )
}

const ActivityTimelineDrawer: React.FC<ActivityTimelineDrawerProps> = ({
  open,
  onClose,
  userId,
  userName,
}) => {
  const [window, setWindow] = React.useState<ActivityWindow>('7d')

  const { data, loading, error } = useDataFetching<ActivityTimelineResponse | null>({
    fetchFn: async () => (open && userId ? activityApi.getTimeline(userId, window) : null),
    dependencies: [open, userId, window],
  })

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: `min(${APP_DRAWER_WIDTH}px, 100vw)`,
          bgcolor: APP_DRAWER_BG,
          boxShadow: APP_DRAWER_SHADOW,
        },
      }}
    >
      <AppDrawerShell>
        <AppDrawerHeader
          eyebrow="USER ACTIVITY"
          title={data?.name || userName || ''}
          subtitle={data ? `${data.email} - ${data.role}` : undefined}
          onClose={onClose}
        />
        <AppDrawerBody padding={{ p: 2 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
            <Typography variant="overline" color="text.secondary">
              Timeline
            </Typography>
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <Select value={window} onChange={(e) => setWindow(e.target.value as ActivityWindow)}>
                {(Object.entries(WINDOW_LABELS) as [ActivityWindow, string][]).map(
                  ([value, label]) => (
                    <MenuItem key={value} value={value}>
                      {label}
                    </MenuItem>
                  )
                )}
              </Select>
            </FormControl>
          </Stack>
          {loading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={28} />
            </Box>
          )}
          {error && (
            <Typography variant="body2" color="error">
              {error}
            </Typography>
          )}
          {!loading && data && data.items.length === 0 && (
            <Typography variant="body2" color="text.secondary">
              No activity in this window.
            </Typography>
          )}
          {!loading && data && data.items.length > 0 && (
            <Stack spacing={0.5}>
              {data.items.map((item, index) => (
                <TimelineRow key={`${item.kind}-${item.at}-${index}`} item={item} />
              ))}
            </Stack>
          )}
        </AppDrawerBody>
      </AppDrawerShell>
    </Drawer>
  )
}

export default ActivityTimelineDrawer
